import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type ApiError } from '../api/client';
import { Alert } from '../components/Field';
import { relativeTime, titleCase } from '../lib/format';

interface NotificationRow {
  id: string;
  event: string;
  title: string;
  body: string;
  linkUrl: string | null;
  read: boolean;
  createdAt: string;
}

interface PreferenceRow {
  event: string;
  essential: boolean;
  channels: Array<{ channel: string; enabled: boolean }>;
}

type Tab = 'inbox' | 'preferences';

/**
 * The notification centre (BRD FR-NTF-005, FR-NTF-006).
 *
 * Preferences sit beside the inbox rather than in account settings, because
 * "stop sending me this" is a thought people have while reading the thing they
 * want to stop receiving.
 */
export function Notifications() {
  const [tab, setTab] = useState<Tab>('inbox');
  const [items, setItems] = useState<NotificationRow[] | null>(null);
  const [unread, setUnread] = useState(0);
  const [total, setTotal] = useState(0);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [preferences, setPreferences] = useState<PreferenceRow[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const navigate = useNavigate();

  const loadInbox = useCallback(() => {
    setItems(null);
    api<{ items: NotificationRow[]; unread: number; total: number }>(
      `/my/notifications?pageSize=50${unreadOnly ? '&unreadOnly=true' : ''}`,
    )
      .then((result) => {
        setItems(result.items);
        setUnread(result.unread);
        setTotal(result.total);
      })
      .catch((caught) => setError(caught as ApiError));
  }, [unreadOnly]);

  const loadPreferences = useCallback(() => {
    api<{ items: PreferenceRow[] }>('/my/notification-preferences')
      .then((result) => setPreferences(result.items))
      .catch((caught) => setError(caught as ApiError));
  }, []);

  useEffect(() => {
    if (tab === 'inbox') loadInbox();
    else loadPreferences();
  }, [tab, loadInbox, loadPreferences]);

  async function open(item: NotificationRow) {
    if (!item.read) {
      try {
        await api(`/my/notifications/${item.id}/read`, { method: 'POST' });
        setItems((current) =>
          current?.map((row) => (row.id === item.id ? { ...row, read: true } : row)) ?? null,
        );
        setUnread((count) => Math.max(0, count - 1));
      } catch (caught) {
        setError(caught as ApiError);
      }
    }
    if (item.linkUrl) navigate(item.linkUrl);
  }

  async function markAll() {
    try {
      const result = await api<{ marked: number }>('/my/notifications/read-all', { method: 'POST' });
      setNotice(result.marked === 0 ? 'Nothing was unread.' : `Marked ${result.marked} as read.`);
      loadInbox();
    } catch (caught) {
      setError(caught as ApiError);
    }
  }

  async function toggle(event: string, channel: string, enabled: boolean) {
    setError(null);
    setNotice(null);
    try {
      await api('/my/notification-preferences', {
        method: 'PUT',
        body: JSON.stringify({ event, channel, enabled }),
      });
      setPreferences((current) =>
        current?.map((row) =>
          row.event === event
            ? {
                ...row,
                channels: row.channels.map((entry) =>
                  entry.channel === channel ? { ...entry, enabled } : entry,
                ),
              }
            : row,
        ) ?? null,
      );
    } catch (caught) {
      setError(caught as ApiError);
    }
  }

  return (
    <>
      <div className="page__head">
        <h1 className="page__title">Notifications</h1>
        <p className="page__lede">
          What the platform has told you, and what you would rather it stopped telling you.
        </p>
      </div>

      {error ? <Alert kind="error" title={error.message} items={error.details} /> : null}
      {notice ? <Alert kind="success" title={notice} /> : null}

      <div className="tabs" role="tablist">
        <button
          role="tab"
          aria-selected={tab === 'inbox'}
          className={`tabs__tab${tab === 'inbox' ? ' tabs__tab--on' : ''}`}
          onClick={() => setTab('inbox')}
        >
          Inbox{unread > 0 ? ` (${unread})` : ''}
        </button>
        <button
          role="tab"
          aria-selected={tab === 'preferences'}
          className={`tabs__tab${tab === 'preferences' ? ' tabs__tab--on' : ''}`}
          onClick={() => setTab('preferences')}
        >
          Preferences
        </button>
      </div>

      {tab === 'inbox' ? (
        <div className="card">
          <div className="card__head">
            <span className="card__title">
              {unreadOnly ? 'Unread' : 'Everything'} {items ? `(${total})` : ''}
            </span>
            <div className="row" style={{ gap: 8 }}>
              <button
                className="btn btn--quiet btn--sm"
                onClick={() => setUnreadOnly((value) => !value)}
              >
                {unreadOnly ? 'Show all' : 'Unread only'}
              </button>
              <button
                className="btn btn--sm"
                onClick={() => void markAll()}
                disabled={unread === 0}
              >
                Mark all read
              </button>
            </div>
          </div>

          {!items ? (
            <div className="spinner">Loading…</div>
          ) : items.length === 0 ? (
            <div className="empty">
              {unreadOnly ? 'Nothing unread.' : 'Nothing here yet.'}
            </div>
          ) : (
            <ul className="notes">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    className={`notes__item${item.read ? '' : ' notes__item--unread'}`}
                    onClick={() => void open(item)}
                  >
                    <span className="notes__dot" aria-hidden="true" />
                    <span className="notes__main">
                      <span className="notes__title">{item.title}</span>
                      <span className="notes__body">{item.body}</span>
                    </span>
                    <span className="notes__meta">
                      <span className="notes__event">{titleCase(item.event)}</span>
                      <span className="muted">{relativeTime(item.createdAt)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="card">
          <div className="card__head">
            <span className="card__title">What you receive</span>
          </div>
          <div className="card__body">
            <p className="muted" style={{ marginTop: 0 }}>
              Transactional and security messages cannot be turned off — you would otherwise miss a
              password reset or a failed payment. Everything else is yours to decide.
            </p>
          </div>

          {!preferences ? (
            <div className="spinner">Loading…</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Notification</th>
                  <th style={{ width: 120 }}>Email</th>
                  <th style={{ width: 120 }}>In-app</th>
                </tr>
              </thead>
              <tbody>
                {preferences.map((row) => (
                  <tr key={row.event}>
                    <td>
                      {titleCase(row.event)}
                      {row.essential ? (
                        <span className="pill pill--quiet" style={{ marginLeft: 8 }}>
                          Always sent
                        </span>
                      ) : null}
                    </td>
                    {row.channels.map((entry) => (
                      <td key={entry.channel}>
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={entry.enabled}
                            disabled={row.essential}
                            onChange={(changed) =>
                              void toggle(row.event, entry.channel, changed.target.checked)
                            }
                          />
                          <span className="switch__track" aria-hidden="true" />
                          <span className="sr-only">
                            {titleCase(row.event)} by {entry.channel}
                          </span>
                        </label>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </>
  );
}
